import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Vessel } from 'icreate/common/models/vessel';
import { EapService } from '@service/eap.service';
import { Eap, eapMax, EapScreenDisplayType, eapScreenDisplay, EapLedPattern } from 'icreate/common/models/eap';
import { MessageDialogService } from '@service/message-dialog.service';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { ConfirmationWindowComponent } from '@components/confirmation-window/confirmation-window.component';
import { MappedErrors } from 'icreate/common/validators/custom-functions';
import _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { EapSocketService } from '@service/eap-socket.service';
import { PatternSelectionDialog } from './pattern-selection-dialog/pattern-selection-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { LEDPatternSettingDialog } from './led-pattern-setting-dialog/led-pattern-setting-dialog.component';

export enum EapInputName {
    location = 'location',
    pattern = 'pattern',
}

@Component({
    selector: 'app-eap-led-settings',
    templateUrl: './eap-led-settings.component.html',
    styleUrls: ['./eap-led-settings.component.css'],
    standalone: false,
})
export class EapLedSettingsComponent implements OnInit {
    readonly EAP_MAX = eapMax;
    readonly LOCATION = EapInputName.location;
    readonly PATTERN = EapInputName.pattern;

    @Input() vessel!: Vessel;
    @Input() userCanEdit: boolean = true;
    @ViewChild('cancelConfirmation') cancelConfirmation: ConfirmationWindowComponent;
    @ViewChild('infoMessage') infoMessage: ElementRef;

    isOpenOption: boolean = false;
    eapLedList: Eap[] = [];
    ledPatternList: EapLedPattern[] = [];
    isLoading: boolean = true;
    initEapLedList: Eap[];
    errorMessagesList: string[] = [];
    showErrors: boolean = false;

    selectedIndex: number = null;

    displays: EapScreenDisplayType[] = [eapScreenDisplay.LCD, eapScreenDisplay.LED];

    validationErrors: { [rowIndex: number]: MappedErrors } = {};
    emptyInputErrors: number = 0;

    private destroySubscriptionSubject$ = new Subject<void>();
    totalEapCount: number;

    constructor(
        private eapService: EapService,
        private eapSocketService: EapSocketService,
        private messageDialogService: MessageDialogService,
        private translateService: TranslateService,
        private dialog: MatDialog
    ) {}

    async ngOnInit() {
        const eapList = await this.eapService.getList(this.vessel.id);
        this.totalEapCount = eapList.length;

        this.eapLedList = eapList.filter(eap => eap.screenDisplay === eapScreenDisplay.LED);
        this.initEapLedList = _.cloneDeep(this.eapLedList);

        this.ledPatternList = await this.eapService.getPatternsList(this.vessel.id);

        this.eapSocketService
            .eapTableUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(newEapList => {
                if (this.vessel.id === newEapList[0].vesselId) {
                    this.totalEapCount = newEapList.length;
                    this.eapLedList = newEapList.filter(eap => eap.screenDisplay === eapScreenDisplay.LED);
                    this.initEapLedList = _.cloneDeep(this.eapLedList);
                }
            });

        this.eapSocketService
            .eapUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((updatedEap: Eap) => {
                if (updatedEap.vesselId !== this.vessel.id) {
                    return;
                }
                this.eapLedList = this.eapLedList.map(eap =>
                    eap.id === updatedEap.id
                        ? {
                              ...eap,
                              ...updatedEap,
                              index: eap.index,
                          }
                        : eap
                );

                this.initEapLedList = _.cloneDeep(this.eapLedList);
            });

        this.eapSocketService
            .ledPatternCreated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((newPattern: EapLedPattern) => {
                if (newPattern.vesselId === this.vessel.id) {
                    this.ledPatternList.push(newPattern);
                    this.messageDialogService.openMessage('system-settings.eap.led.pattern.created', {
                        name: newPattern.name,
                    });
                }
            });

        this.eapSocketService
            .ledPatternUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((updatedPattern: EapLedPattern) => {
                if (updatedPattern.vesselId === this.vessel.id) {
                    const index = this.ledPatternList.findIndex(pattern => pattern.id === updatedPattern.id);
                    if (index !== -1) {
                        this.ledPatternList[index] = updatedPattern;
                        this.messageDialogService.openMessage('system-settings.eap.led.pattern.updated', {
                            name: updatedPattern.name,
                        });
                    }
                }
            });

        this.eapSocketService
            .ledPatternDeleted$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((deletedPattern: EapLedPattern) => {
                if (deletedPattern.vesselId !== this.vessel.id) {
                    return;
                }
                const index = this.ledPatternList.findIndex(pattern => pattern.id === deletedPattern.id);
                if (index !== -1) {
                    this.ledPatternList.splice(index, 1);
                    this.messageDialogService.openMessage('system-settings.eap.led.pattern.deleted', {
                        name: deletedPattern.name,
                    });
                }
            });

        this.isLoading = false;
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    getPatternText(eap: Eap): string {
        if (eap.eapLedPatternId) {
            const pattern = this.ledPatternList.find(ledPattern => ledPattern.id === eap.eapLedPatternId);
            if (pattern) {
                return pattern.name;
            }
        }
    }

    getPatternButtonText(eap: Eap): string {
        if (eap.eapLedPatternId) {
            return this.translateService.instant('system-settings.eap.led.table-header.change.text-button');
        }
        return this.translateService.instant('system-settings.eap.led.table-header.assign.text-button');
    }

    async selectPattern(eap: Eap) {
        const dialogRef = this.dialog.open(PatternSelectionDialog, {
            disableClose: true,
            width: '474px',
            height: '284px',
            data: {
                eap,
                ledPatternList: this.ledPatternList,
            },
        });
        const newPattern = await lastValueFrom(dialogRef.afterClosed());
        if (newPattern) {
            await this.eapService.update(eap);
            this.messageDialogService.openMessage('system-settings.eap.pattern-assignment.updated', {
                eap: eap.index,
            });
        }
    }

    showMessageDialog(message: string, params?: Record<string, any>) {
        this.messageDialogService.openMessage(message, params);
    }

    showLedPatternSettings() {
        this.dialog.open(LEDPatternSettingDialog, {
            width: '474px',
            disableClose: false,
            data: {
                ledPatternList: this.ledPatternList,
                vesselId: this.vessel.id,
                userCanEdit: this.userCanEdit,
            },
        });
    }
}
